import { FC, useEffect, useState } from 'react';

import {
  Control,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  HStack,
  Switch,
  AccordionIcon,
  AccordionPanel,
  Divider,
  Box,
} from '@chakra-ui/react';

import { ActionList } from '.';
import { RoleForm } from '../models';
import { useGetAllActionsQuery } from '../../../services';
import { Permission } from '../../../models';

export const PermissionList: FC<{
  control: Control<RoleForm, void>;
  setValue: UseFormSetValue<RoleForm>;
  watch: UseFormWatch<RoleForm>;
}> = ({ control, setValue, watch }) => {
  const [indexes, setIndexes] = useState<number[]>([]);
  const [checkedPermissions, setCheckedPermissions] = useState<boolean[]>([]);

  const { data, isLoading } = useGetAllActionsQuery();

  const { fields } = useFieldArray({
    control,
    name: 'permission',
  });

  useEffect(() => {
    const permissions = data?.data.map(
      ({ id: actionId, permissionName, actions }) => {
        return {
          id: actionId,
          permissionName,
          permissionActions: actions.map(({ id, description }) => ({
            action: { id, description },
            enabled: 'false',
          })),
        };
      }
    ) as Permission[];

    setCheckedPermissions(
      new Array(data?.data.length).fill(false, 0, data?.data.length)
    );

    setValue('permission', permissions);
  }, [data?.data]);

  return (
    <Accordion allowMultiple index={indexes}>
      {fields.map(({ id, permissionName }, accordionIndex) => (
        <AccordionItem key={id}>
          {({ isExpanded }) => (
            <>
              <h2>
                <AccordionButton
                  bg="none"
                  _focus={{ bg: 'none' }}
                  paddingInlineStart="0"
                  justifyContent="space-between"
                  onClick={e => {
                    if ((e.target as HTMLElement).closest('.chakra-switch'))
                      return;
                    const isIndexExist =
                      indexes.findIndex(index => index === accordionIndex) !==
                      -1;
                    setIndexes(indexes => {
                      if (isIndexExist)
                        return indexes.filter(
                          index => index !== accordionIndex
                        );
                      else return indexes.concat(accordionIndex);
                    });
                  }}
                >
                  <HStack>
                    <Box
                      as="span"
                      flex="1"
                      textAlign="left"
                      color="custom_blue.1"
                      fontWeight="bold"
                      fontSize="md"
                    >
                      {permissionName}
                    </Box>
                    {isExpanded && (
                      <Switch
                        isDisabled={isLoading}
                        colorScheme="green"
                        isChecked={
                          checkedPermissions[accordionIndex] ||
                          watch(
                            `permission.${accordionIndex}.permissionActions`
                          )
                            .map(({ enabled }) => enabled)
                            .every(enabled => enabled === 'true')
                        }
                        onChange={e => {
                          checkedPermissions[accordionIndex] = e.target.checked;
                          fields[accordionIndex].permissionActions.forEach(
                            (_, index) => {
                              setValue(
                                `permission.${accordionIndex}.permissionActions.${index}.enabled`,
                                e.target.checked ? 'true' : 'false'
                              );
                            }
                          );
                        }}
                      />
                    )}
                  </HStack>
                  <AccordionIcon color="custom.gary.1" />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4} paddingInlineStart="0">
                <Box>
                  <ActionList nestIndex={accordionIndex} {...{ control }} />
                  {Number(data?.data.length) - 1 > accordionIndex && (
                    <Divider my="3" />
                  )}
                </Box>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      ))}
    </Accordion>
  );
};
